import {
  buildTenantWebConfig,
  tenantIdFromHostname,
  type TenantRegion,
  type TenantWebConfig,
} from "@kovernow/types/tenant-web-config";
import { TENANT_BRAND_FILES } from "../tenants";

/**
 * Turn `/config.json` + the hostname into what the app should render
 * (CAP-1). Pure, so it is unit-tested without a browser.
 */
export type Resolution =
  | { kind: "app"; config: TenantWebConfig }
  | { kind: "picker"; config: TenantWebConfig; regions: Partial<Record<TenantRegion, string>> }
  | { kind: "redirect"; url: string }
  | { kind: "error"; reason: string };

export const REGION_CHOICE_KEY = "kn.region";

export interface ResolveInput {
  runtime: unknown;
  hostname: string;
  /** Path + search + hash to keep when redirecting a picker visitor. */
  path: string;
  /** Remembered region choice (localStorage), if any. */
  rememberedRegion: string | null;
  /** Dev server on localhost may skip the host cross-check. */
  allowLocalhost: boolean;
}

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);

export function resolveConfig(input: ResolveInput): Resolution {
  const runtime = input.runtime as { tenantId?: unknown } | null;
  if (!runtime || typeof runtime !== "object" || typeof runtime.tenantId !== "string") {
    return { kind: "error", reason: "config_invalid" };
  }
  const brand = TENANT_BRAND_FILES[runtime.tenantId];
  if (!brand) return { kind: "error", reason: `unknown_tenant:${runtime.tenantId}` };
  const built = buildTenantWebConfig(runtime, brand);
  if (!built.ok) return { kind: "error", reason: `config_invalid:${built.errors[0]}` };
  const config = built.config;
  const host = input.hostname.toLowerCase();

  // Region picker host (app.kovernow.com): remembered choice → redirect.
  const picker = config.regionPicker;
  if (picker && picker.hosts.map((h) => h.toLowerCase()).includes(host)) {
    const remembered = input.rememberedRegion;
    // Own keys only: a stored "constructor" must not hit Object.prototype.
    const target =
      remembered && Object.hasOwn(picker.regions, remembered)
        ? picker.regions[remembered as TenantRegion]
        : undefined;
    if (target) return { kind: "redirect", url: target.replace(/\/+$/, "") + input.path };
    return { kind: "picker", config, regions: picker.regions };
  }

  if (input.allowLocalhost && LOCAL_HOSTS.has(host)) return { kind: "app", config };

  // Each stack serves only its own host: /config.json is authoritative and
  // the hostname must agree with it. A mismatch is a hard error.
  if (!config.hosts.map((h) => h.toLowerCase()).includes(host)) {
    return { kind: "error", reason: `host_not_configured:${host}` };
  }
  const hostTenant = tenantIdFromHostname(host);
  if (hostTenant !== config.tenantId) {
    return { kind: "error", reason: `host_tenant_mismatch:${host}` };
  }
  return { kind: "app", config };
}
